import React from 'react';
import { AlertCircle, X } from 'lucide-react';

/**
 * Confirmation dialog component
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message
 * @param {string} confirmText - Confirm button text (default: "Potwierdź")
 * @param {string} cancelText - Cancel button text (default: "Anuluj")
 * @param {function} onConfirm - Callback when user confirms
 * @param {function} onCancel - Callback when user cancels
 * @param {string} variant - Visual variant: "danger" | "warning" (default: "warning")
 */
export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Potwierdź',
  cancelText = 'Anuluj',
  onConfirm,
  onCancel,
  variant = 'warning'
}) {
  if (!isOpen) return null;

  const colors = {
    danger: {
      icon: 'text-red-600',
      bg: 'bg-red-100',
      border: 'border-red-200',
      button: 'bg-gradient-to-br from-red-600 to-red-700 hover:shadow-lg hover:shadow-red-500/30 border border-red-500/20'
    },
    warning: {
      icon: 'text-amber-600',
      bg: 'bg-amber-100',
      border: 'border-amber-200',
      button: 'bg-gradient-to-br from-amber-600 to-amber-700 hover:shadow-lg hover:shadow-amber-500/30 border border-amber-500/20'
    }
  };

  const style = colors[variant];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] max-w-md w-full p-6 md:p-8">
        {/* Close button */}
        <div className="flex justify-end mb-2">
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors duration-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Header - centered icon and title */}
        <div className="flex flex-col items-center mb-6">
          <div className={`w-14 h-14 ${style.bg} ${style.border} border rounded-full flex items-center justify-center mb-4 shadow-sm`}>
            <AlertCircle className={`w-7 h-7 ${style.icon}`} />
          </div>
          <h3 className="text-xl font-bold text-slate-900 text-center">{title}</h3>
        </div>

        {/* Message */}
        <p className="text-slate-600 mb-6 leading-relaxed text-center">{message}</p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-6 py-3 bg-slate-50 text-slate-800 rounded-lg font-semibold hover:bg-slate-100 border border-slate-200 transition-all duration-300 shadow-sm hover:shadow-md"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-6 py-3 text-white rounded-lg font-semibold transition-all duration-300 ${style.button}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
